// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/ClinicProfile/sections/SpecialHoursSection.tsx

import React, { useEffect, useState } from "react";
import {
  ClinicAdminClinicHoursByDay,
  ClinicAdminClinicHoursSettings,
  ClinicAdminClinicSettings,
} from "../../../../../api/clinicAdmin";

type DayKey = "0" | "1" | "2" | "3" | "4" | "5" | "6";

const DAY_LABELS: Record<DayKey, string> = {
  "0": "Sunday",
  "1": "Monday",
  "2": "Tuesday",
  "3": "Wednesday",
  "4": "Thursday",
  "5": "Friday",
  "6": "Saturday",
};

function asString(v: any): string {
  return String(v ?? "").trim();
}

function isValidIsoDate(d: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  const dt = new Date(`${d}T00:00:00Z`);
  if (Number.isNaN(dt.getTime())) return false;
  return dt.toISOString().slice(0, 10) === d;
}

function isValidTimeHHMM(t: string): boolean {
  if (!/^\d{2}:\d{2}$/.test(t)) return false;
  const [hh, mm] = t.split(":").map((x) => parseInt(x, 10));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
  return hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59;
}

function sortClosures(a: { date: string }, b: { date: string }) {
  return a.date.localeCompare(b.date);
}

function renderHoursByDay(hoursByDay?: Partial<ClinicAdminClinicHoursByDay>) {
  if (!hoursByDay || !Object.keys(hoursByDay).length) {
    return <div className="ca-clinic-profile__muted">—</div>;
  }

  return (
    <div className="ca-clinic-profile__stack">
      {(Object.keys(DAY_LABELS) as DayKey[]).map((k) => {
        const h = hoursByDay[k];
        if (!h) return null;

        return (
          <div key={k} className="ca-clinic-profile__inlineRow">
            <span className="ca-clinic-profile__inlineLabel">
              {DAY_LABELS[k]}:
            </span>
            <span>{h.isClosed ? "Closed" : `${h.open} – ${h.close}`}</span>
          </div>
        );
      })}
    </div>
  );
}

function emptyOverrideHours(): Partial<ClinicAdminClinicHoursByDay> {
  // Default to a complete, explicit 7-day definition (closed by default).
  const out: Partial<ClinicAdminClinicHoursByDay> = {};
  (Object.keys(DAY_LABELS) as DayKey[]).forEach((k) => {
    out[k] = { isClosed: true };
  });
  return out;
}

export default function SpecialHoursSection(props: {
  clinicSettings: ClinicAdminClinicSettings;

  // Editing + save (controlled by parent page)
  editMode: boolean;

  draftSpecialHours: ClinicAdminClinicHoursSettings;
  setDraftSpecialHours: React.Dispatch<
    React.SetStateAction<ClinicAdminClinicHoursSettings>
  >;

  savingSpecialHours: boolean;
  specialHoursError: string | null;
  specialHoursSavedAt: string | null;

  onSaveSpecialHours: () => void;
}) {
  const {
    clinicSettings,
    editMode,
    draftSpecialHours,
    setDraftSpecialHours,
    savingSpecialHours,
    specialHoursError,
    specialHoursSavedAt,
    onSaveSpecialHours,
  } = props;

  const hours = clinicSettings?.hours;

  const hasClosures =
    Array.isArray(hours?.closures) && (hours?.closures?.length || 0) > 0;
  const hasTempOverride = !!hours?.temporaryOverride;
  const hasWalkIn = !!hours?.walkIn?.enabled;

  // Local draft helpers for add-row UX
  const [newClosureDate, setNewClosureDate] = useState<string>("");
  const [newClosureReason, setNewClosureReason] = useState<string>("");

  const hasDraftClosures =
    Array.isArray(draftSpecialHours?.closures) &&
    (draftSpecialHours?.closures?.length || 0) > 0;
  const hasDraftOverride = !!draftSpecialHours?.temporaryOverride;
  const hasDraftWalkIn = !!draftSpecialHours?.walkIn?.enabled;

  useEffect(() => {
    if (!editMode) {
      setNewClosureDate("");
      setNewClosureReason("");
    }
  }, [editMode]);

  function addClosure() {
    const date = asString(newClosureDate);
    const reason = asString(newClosureReason);

    if (!isValidIsoDate(date)) return;

    setDraftSpecialHours((prev) => {
      const current = Array.isArray(prev?.closures) ? prev!.closures! : [];
      if (current.some((c) => c.date === date)) return prev;

      const next = [...current, { date, reason: reason.length ? reason : null }];
      next.sort(sortClosures);

      return {
        ...(prev || {}),
        closures: next,
      };
    });

    setNewClosureDate("");
    setNewClosureReason("");
  }

  function removeClosure(date: string) {
    setDraftSpecialHours((prev) => {
      const current = Array.isArray(prev?.closures) ? prev!.closures! : [];
      const next = current.filter((c) => c.date !== date);
      return { ...(prev || {}), closures: next };
    });
  }

  function toggleTemporaryOverride(enabled: boolean) {
    setDraftSpecialHours((prev) => {
      const base = prev || {};
      if (!enabled) {
        return { ...base, temporaryOverride: null };
      }
      return {
        ...base,
        temporaryOverride: {
          start: "",
          end: "",
          notePublic: null,
          hoursByDay: emptyOverrideHours(),
        },
      };
    });
  }

  function toggleWalkIn(enabled: boolean) {
    setDraftSpecialHours((prev) => {
      const base = prev || {};
      if (!enabled) {
        return {
          ...base,
          walkIn: {
            enabled: false,
            notePublic: null,
            hoursByDay: {},
          },
        };
      }
      return {
        ...base,
        walkIn: {
          enabled: true,
          notePublic: null,
          hoursByDay: emptyOverrideHours(),
        },
      };
    });
  }

  function updateOverrideField<
    K extends keyof NonNullable<ClinicAdminClinicHoursSettings["temporaryOverride"]>
  >(key: K, value: any) {
    setDraftSpecialHours((prev) => {
      const o = prev?.temporaryOverride || null;
      if (!o) return prev;
      return {
        ...(prev || {}),
        temporaryOverride: { ...o, [key]: value },
      };
    });
  }

  function updateWalkInField<
    K extends keyof NonNullable<ClinicAdminClinicHoursSettings["walkIn"]>
  >(key: K, value: any) {
    setDraftSpecialHours((prev) => {
      const w =
        prev?.walkIn ||
        ({ enabled: false, notePublic: null, hoursByDay: {} } as any);
      return {
        ...(prev || {}),
        walkIn: { ...w, [key]: value },
      };
    });
  }

  function updateDayHours(
    which: "temporaryOverride" | "walkIn",
    day: DayKey,
    patch: Partial<{ isClosed: boolean; open: string; close: string }>
  ) {
    setDraftSpecialHours((prev) => {
      const base = prev || {};

      if (which === "temporaryOverride") {
        const o = base.temporaryOverride;
        if (!o) return prev;

        const hoursByDay = {
          ...(o.hoursByDay || {}),
        } as Partial<ClinicAdminClinicHoursByDay>;
        const current = hoursByDay[day] || { isClosed: true };
        const isClosed =
          patch.isClosed !== undefined ? !!patch.isClosed : !!current.isClosed;

        if (isClosed) {
          hoursByDay[day] = { isClosed: true };
        } else {
          const open =
            patch.open !== undefined
              ? patch.open
              : (current as any).open || "09:00";
          const close =
            patch.close !== undefined
              ? patch.close
              : (current as any).close || "17:00";
          hoursByDay[day] = { isClosed: false, open, close };
        }

        return {
          ...base,
          temporaryOverride: { ...o, hoursByDay },
        };
      }

      // walkIn
      const w =
        base.walkIn ||
        ({ enabled: false, notePublic: null, hoursByDay: {} } as any);
      const hoursByDay = {
        ...(w.hoursByDay || {}),
      } as Partial<ClinicAdminClinicHoursByDay>;
      const current = hoursByDay[day] || { isClosed: true };
      const isClosed =
        patch.isClosed !== undefined ? !!patch.isClosed : !!current.isClosed;

      if (isClosed) {
        hoursByDay[day] = { isClosed: true };
      } else {
        const open =
          patch.open !== undefined
            ? patch.open
            : (current as any).open || "09:00";
        const close =
          patch.close !== undefined
            ? patch.close
            : (current as any).close || "17:00";
        hoursByDay[day] = { isClosed: false, open, close };
      }

      return {
        ...base,
        walkIn: { ...w, hoursByDay },
      };
    });
  }

  function validateDraft(): string | null {
    // Closures
    const closures = Array.isArray(draftSpecialHours?.closures)
      ? draftSpecialHours!.closures!
      : [];
    for (const c of closures) {
      if (!c?.date || !isValidIsoDate(String(c.date))) {
        return "Closures: each item must include a valid date (YYYY-MM-DD).";
      }
      const reason = asString((c as any).reason);
      if (reason.length > 120) {
        return "Closures: reason must be 120 characters or less.";
      }
    }

    // Temporary override
    const o = draftSpecialHours?.temporaryOverride || null;
    if (o) {
      const start = asString(o.start);
      const end = asString(o.end);
      if (!isValidIsoDate(start) || !isValidIsoDate(end)) {
        return "Temporary override: start and end must be valid dates (YYYY-MM-DD).";
      }
      if (start > end) return "Temporary override: start must be on or before end.";

      const note = asString(o.notePublic);
      if (note.length > 120)
        return "Temporary override: note must be 120 characters or less.";

      const hbd = o.hoursByDay || {};
      if (!Object.keys(hbd).length) {
        return "Temporary override: please set hours for at least one day.";
      }

      for (const k of Object.keys(hbd)) {
        if (!Object.prototype.hasOwnProperty.call(DAY_LABELS, k)) {
          return "Temporary override: hours must use day keys 0..6.";
        }
        const h = (hbd as any)[k];
        if (!h || typeof h.isClosed !== "boolean") {
          return "Temporary override: each day must specify isClosed.";
        }
        if (!h.isClosed) {
          if (
            !isValidTimeHHMM(asString(h.open)) ||
            !isValidTimeHHMM(asString(h.close))
          ) {
            return "Temporary override: open/close times must be HH:MM.";
          }
          if (asString(h.close) <= asString(h.open)) {
            return "Temporary override: close time must be after open time.";
          }
        }
      }
    }

    // Walk-in (only validate hours if enabled)
    const w = draftSpecialHours?.walkIn || null;
    if (w && w.enabled) {
      const note = asString(w.notePublic);
      if (note.length > 140) return "Walk-in note must be 140 characters or less.";

      const hbd = w.hoursByDay || {};
      if (!Object.keys(hbd).length) {
        return "Walk-in hours: please set hours for at least one day, or disable walk-in hours.";
      }

      for (const k of Object.keys(hbd)) {
        if (!Object.prototype.hasOwnProperty.call(DAY_LABELS, k)) {
          return "Walk-in hours: hours must use day keys 0..6.";
        }
        const h = (hbd as any)[k];
        if (!h || typeof h.isClosed !== "boolean") {
          return "Walk-in hours: each day must specify isClosed.";
        }
        if (!h.isClosed) {
          if (
            !isValidTimeHHMM(asString(h.open)) ||
            !isValidTimeHHMM(asString(h.close))
          ) {
            return "Walk-in hours: open/close times must be HH:MM.";
          }
          if (asString(h.close) <= asString(h.open)) {
            return "Walk-in hours: close time must be after open time.";
          }
        }
      }
    }

    return null;
  }

  function onClickSave() {
    const err = validateDraft();
    if (err) {
      // Parent displays error; local validation prevents the save click from firing.
      return;
    }
    onSaveSpecialHours();
  }

  const displayHours = editMode ? { ...draftSpecialHours } : hours || ({} as any);

  const dispHasClosures =
    Array.isArray((displayHours as any)?.closures) &&
    ((displayHours as any)?.closures?.length || 0) > 0;
  const dispHasTempOverride = !!(displayHours as any)?.temporaryOverride;
  const dispHasWalkIn = !!(displayHours as any)?.walkIn?.enabled;

  return (
    <details className="ca-clinic-profile__panel" open>
      <summary className="ca-clinic-profile__summary">
        <div>
          <div className="ca-clinic-profile__sectionTitle">Special hours</div>
          <div className="ca-clinic-profile__hint">
            Closures, holidays, and temporary schedule changes
          </div>
        </div>
      </summary>

      {editMode && specialHoursError && (
        <div className="ca-clinic-profile__editActions">
          <div className="ca-clinic-profile__error">{specialHoursError}</div>
        </div>
      )}

      {/* Closures */}
      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Closures & holidays</div>
        <div className="ca-clinic-profile__value">
          {dispHasClosures ? (
            <div className="ca-clinic-profile__stack">
              {(Array.isArray((displayHours as any)?.closures)
                ? (displayHours as any).closures
                : []
              )
                .slice()
                .sort(sortClosures)
                .map((c: any, idx: number) => (
                  <div
                    key={`${c.date}-${idx}`}
                    style={{ display: "flex", alignItems: "baseline", gap: 10 }}
                  >
                    <div>
                      {c.date}
                      {c.reason ? ` — ${c.reason}` : ""}
                    </div>

                    {editMode && (
                      <button
                        className="ca-clinic-profile__linkBtn"
                        type="button"
                        onClick={() => removeClosure(c.date)}
                        disabled={savingSpecialHours}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            "—"
          )}

          {editMode && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label className="ca-clinic-profile__muted">Date</label>
                  <input
                    className="ca-clinic-profile__input"
                    type="date"
                    value={newClosureDate}
                    onChange={(e) => setNewClosureDate(e.target.value)}
                    disabled={savingSpecialHours}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    minWidth: 260,
                  }}
                >
                  <label className="ca-clinic-profile__muted">
                    Reason (optional)
                  </label>
                  <input
                    className="ca-clinic-profile__input"
                    type="text"
                    placeholder="e.g., Family Day"
                    value={newClosureReason}
                    onChange={(e) => setNewClosureReason(e.target.value)}
                    disabled={savingSpecialHours}
                    maxLength={120}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "end" }}>
                  <button
                    className="ca-clinic-profile__btn ca-clinic-profile__btn--ghost"
                    type="button"
                    onClick={addClosure}
                    disabled={
                      savingSpecialHours ||
                      !newClosureDate ||
                      !isValidIsoDate(newClosureDate)
                    }
                  >
                    Add closure
                  </button>
                </div>
              </div>

              {hasDraftClosures && (
                <div className="ca-clinic-profile__muted" style={{ marginTop: 6 }}>
                  Tip: closures are public on your clinic page.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Temporary override */}
      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Temporary hours override</div>
        <div className="ca-clinic-profile__value">
          {!editMode ? (
            dispHasTempOverride ? (
              <div className="ca-clinic-profile__stack">
                <div>
                  {(displayHours as any).temporaryOverride.start} →{" "}
                  {(displayHours as any).temporaryOverride.end}
                </div>
                {(displayHours as any).temporaryOverride.notePublic && (
                  <div className="ca-clinic-profile__muted">
                    {(displayHours as any).temporaryOverride.notePublic}
                  </div>
                )}
                {renderHoursByDay(
                  (displayHours as any).temporaryOverride.hoursByDay
                )}
              </div>
            ) : (
              "—"
            )
          ) : (
            <div className="ca-clinic-profile__stack">
              <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!draftSpecialHours?.temporaryOverride}
                  onChange={(e) => toggleTemporaryOverride(e.target.checked)}
                  disabled={savingSpecialHours}
                />
                Enable temporary override
              </label>

              {hasDraftOverride ? (
                <>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div
                      style={{ display: "flex", flexDirection: "column", gap: 6 }}
                    >
                      <label className="ca-clinic-profile__muted">Start</label>
                      <input
                        className="ca-clinic-profile__input"
                        type="date"
                        value={asString(draftSpecialHours.temporaryOverride?.start)}
                        onChange={(e) => updateOverrideField("start", e.target.value)}
                        disabled={savingSpecialHours}
                      />
                    </div>

                    <div
                      style={{ display: "flex", flexDirection: "column", gap: 6 }}
                    >
                      <label className="ca-clinic-profile__muted">End</label>
                      <input
                        className="ca-clinic-profile__input"
                        type="date"
                        value={asString(draftSpecialHours.temporaryOverride?.end)}
                        onChange={(e) => updateOverrideField("end", e.target.value)}
                        disabled={savingSpecialHours}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        minWidth: 260,
                      }}
                    >
                      <label className="ca-clinic-profile__muted">
                        Public note (optional)
                      </label>
                      <input
                        className="ca-clinic-profile__input"
                        type="text"
                        placeholder="e.g., Summer hours"
                        value={asString(draftSpecialHours.temporaryOverride?.notePublic)}
                        onChange={(e) => updateOverrideField("notePublic", e.target.value)}
                        disabled={savingSpecialHours}
                        maxLength={120}
                      />
                    </div>
                  </div>

                  <div className="ca-clinic-profile__muted">
                    Set hours for each day during the override period.
                  </div>

                  <div className="ca-clinic-profile__stack">
                    {(Object.keys(DAY_LABELS) as DayKey[]).map((day) => {
                      const row =
                        (draftSpecialHours.temporaryOverride?.hoursByDay as any)?.[day] ||
                        ({ isClosed: true } as any);

                      const isClosed = !!row.isClosed;
                      const open = !isClosed ? asString((row as any).open) : "";
                      const close = !isClosed ? asString((row as any).close) : "";

                      return (
                        <div
                          key={`ovr-${day}`}
                          className="ca-clinic-profile__inlineRow"
                          style={{ gap: 10, flexWrap: "wrap" }}
                        >
                          <div style={{ minWidth: 110 }}>
                            <strong>{DAY_LABELS[day]}</strong>
                          </div>

                          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={isClosed}
                              onChange={(e) =>
                                updateDayHours("temporaryOverride", day, {
                                  isClosed: e.target.checked,
                                })
                              }
                              disabled={savingSpecialHours}
                            />
                            Closed
                          </label>

                          {!isClosed && (
                            <>
                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <span className="ca-clinic-profile__muted">Open</span>
                                <input
                                  className="ca-clinic-profile__input"
                                  type="time"
                                  value={open}
                                  onChange={(e) =>
                                    updateDayHours("temporaryOverride", day, {
                                      open: e.target.value,
                                    })
                                  }
                                  disabled={savingSpecialHours}
                                />
                              </div>

                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <span className="ca-clinic-profile__muted">Close</span>
                                <input
                                  className="ca-clinic-profile__input"
                                  type="time"
                                  value={close}
                                  onChange={(e) =>
                                    updateDayHours("temporaryOverride", day, {
                                      close: e.target.value,
                                    })
                                  }
                                  disabled={savingSpecialHours}
                                />
                              </div>

                              {!isValidTimeHHMM(open) || !isValidTimeHHMM(close) ? (
                                <span className="ca-clinic-profile__muted">Use HH:MM</span>
                              ) : null}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="ca-clinic-profile__muted">—</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Walk-in hours */}
      <div className="ca-clinic-profile__row">
        <div className="ca-clinic-profile__label">Walk-in hours</div>
        <div className="ca-clinic-profile__value">
          {!editMode ? (
            dispHasWalkIn ? (
              <div className="ca-clinic-profile__stack">
                {(displayHours as any).walkIn?.notePublic && (
                  <div className="ca-clinic-profile__muted">
                    {(displayHours as any).walkIn.notePublic}
                  </div>
                )}
                {renderHoursByDay((displayHours as any).walkIn?.hoursByDay)}
              </div>
            ) : (
              "—"
            )
          ) : (
            <div className="ca-clinic-profile__stack">
              <label style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!draftSpecialHours?.walkIn?.enabled}
                  onChange={(e) => toggleWalkIn(e.target.checked)}
                  disabled={savingSpecialHours}
                />
                Walk-in hours are different from regular hours
              </label>

              {hasDraftWalkIn ? (
                <>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        minWidth: 320,
                      }}
                    >
                      <label className="ca-clinic-profile__muted">Public note (optional)</label>
                      <input
                        className="ca-clinic-profile__input"
                        type="text"
                        placeholder="e.g., Walk-in hours may be limited when capacity is reached."
                        value={asString(draftSpecialHours?.walkIn?.notePublic)}
                        onChange={(e) => updateWalkInField("notePublic", e.target.value)}
                        disabled={savingSpecialHours}
                        maxLength={140}
                      />
                    </div>
                  </div>

                  <div className="ca-clinic-profile__muted">Set walk-in hours by day.</div>

                  <div className="ca-clinic-profile__stack">
                    {(Object.keys(DAY_LABELS) as DayKey[]).map((day) => {
                      const row =
                        (draftSpecialHours.walkIn?.hoursByDay as any)?.[day] ||
                        ({ isClosed: true } as any);

                      const isClosed = !!row.isClosed;
                      const open = !isClosed ? asString((row as any).open) : "";
                      const close = !isClosed ? asString((row as any).close) : "";

                      return (
                        <div
                          key={`walkin-${day}`}
                          className="ca-clinic-profile__inlineRow"
                          style={{ gap: 10, flexWrap: "wrap" }}
                        >
                          <div style={{ minWidth: 110 }}>
                            <strong>{DAY_LABELS[day]}</strong>
                          </div>

                          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={isClosed}
                              onChange={(e) =>
                                updateDayHours("walkIn", day, { isClosed: e.target.checked })
                              }
                              disabled={savingSpecialHours}
                            />
                            Closed
                          </label>

                          {!isClosed && (
                            <>
                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <span className="ca-clinic-profile__muted">Open</span>
                                <input
                                  className="ca-clinic-profile__input"
                                  type="time"
                                  value={open}
                                  onChange={(e) =>
                                    updateDayHours("walkIn", day, { open: e.target.value })
                                  }
                                  disabled={savingSpecialHours}
                                />
                              </div>

                              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <span className="ca-clinic-profile__muted">Close</span>
                                <input
                                  className="ca-clinic-profile__input"
                                  type="time"
                                  value={close}
                                  onChange={(e) =>
                                    updateDayHours("walkIn", day, { close: e.target.value })
                                  }
                                  disabled={savingSpecialHours}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="ca-clinic-profile__muted">—</div>
              )}
            </div>
          )}
        </div>
      </div>

      {!editMode && (hasClosures || hasTempOverride || hasWalkIn) && (
        <div className="ca-clinic-profile__muted" style={{ marginTop: 10 }}>
          These special hours may be shown publicly on your clinic page.
        </div>
      )}

      {/* Save row moved to bottom (to match other sections) */}
      {editMode ? (
        <div className="ca-clinic-profile__row">
          <div className="ca-clinic-profile__label"></div>
          <div className="ca-clinic-profile__value ca-clinic-profile__save-row">
            <button
              className="ca-clinic-profile__btn"
              onClick={onClickSave}
              disabled={savingSpecialHours}
            >
              {savingSpecialHours ? "Saving…" : "Save special hours"}
            </button>

            {specialHoursSavedAt ? (
              <span className="ca-clinic-profile__savedAt">
                Saved {new Date(specialHoursSavedAt).toLocaleString()}
              </span>
            ) : null}

            {specialHoursError ? (
              <div className="ca-clinic-profile__row-error" style={{ marginTop: 8 }}>
                {specialHoursError}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </details>
  );
}
